/**
 * @author Nuwandi Hewamadduma
 * @version 0.0.1
 * @since 23/4/2025
 */

import React, { useState, useEffect, useRef } from "react";
import { Stack, TextField, Button, FormControl } from "@mui/material";
import { useLocation } from "react-router-dom";
import OrgsDropdown from "../../../components/orgsDropdown/OrgsDropdown";
import GroupsDropdown from "../../../components/groupsDropdown/GroupsDropdown";
import FilterByDropdown from "../../../components/filterByDropdown/FilterByDropdown";
import ApplicationReportTable from "./ApplicationReportTable";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import Select from "react-select";
import api from "../../../../base/services/ResApi";
import { isFilterReady } from "../../../components/filterByDropdown/filterValidation";

const ApplicationReport = () => {
  const selectOrgRef = useRef(null);
  const selectGrpRef = useRef(null);
  const selectFilterTypeRef = useRef(null);

  const [orgCode, setOrgCode] = useState();
  const [grpId, setGrpId] = useState();
  const [terminalSerial, setTerminalSerial] = useState();

  const [filterByType, setFilterByType] = useState();
  const [filterByVal, setFilterByVal] = useState();
  const [approvalStatus, setApprovalStatus] = useState("approved");
  const [isOrgDisabled, setIsOrgDisabled] = useState(true);
  const [isGrpDisabled, setIsGrpDisabled] = useState(true);
  const [istrmnlDisabled, setIsTrmnlDisabled] = useState(true);

  const [applicationReportData, setApplicationReportData] = useState([]);
  const [loading, setLoading] = useState(false);

  const location = useLocation();
  useEffect(() => {
    if (location.state) {
      const fType = location.state.filterByType;
      setOrgCode(location.state.orgCode);
      setGrpId(location.state.groupId);
      setFilterByVal(location.state.groupId);
      setFilterByType(fType);
      if (fType === "F_GRP_ID") {
        setIsOrgDisabled(false);
        setIsGrpDisabled(false);
        setIsTrmnlDisabled(true);
      } else {
        setIsOrgDisabled(true);
        setIsGrpDisabled(true);
        setIsTrmnlDisabled(true);
      }
    }
  }, [location.state]);

  const handleFilterByChange = (e) => {
    selectOrgRef.current.clearValue();
    selectGrpRef.current.clearValue();
    const value = e?.value;
    setFilterByType(value);
    setOrgCode(undefined);
    setGrpId(undefined);
    setTerminalSerial("");
    setFilterByVal(undefined);
    if (value === "F_ORG_CODE") {
      setIsOrgDisabled(false);
      setIsGrpDisabled(true);
      setIsTrmnlDisabled(true);
    } else if (value === "F_GRP_ID") {
      setIsOrgDisabled(false);
      setIsGrpDisabled(false);
      setIsTrmnlDisabled(true);
    } else if (value === "F_TRMNL_SERIAL") {
      setIsOrgDisabled(true);
      setIsGrpDisabled(true);
      setIsTrmnlDisabled(false);
    } else {
      setIsOrgDisabled(true);
      setIsGrpDisabled(true);
      setIsTrmnlDisabled(true);
    }
  };

  const handleOrgChange = (event) => {
    const value = event?.value;
    selectGrpRef.current?.clearValue();
    setGrpId(undefined);
    setFilterByVal(filterByType === "F_ORG_CODE" ? value : undefined);
    setOrgCode(value);
  };

  const handleGrpChange = (event) => {
    const value = event?.value;
    setFilterByVal(value);
    setGrpId(value);
  };

  const handleTerminalSerialChange = (e) => {
    const value = e.target.value;
    setFilterByVal(value);
    setTerminalSerial(value);
  };

  const clearFilters = () => {
    selectOrgRef.current.clearValue();
    selectGrpRef.current.clearValue();
    selectFilterTypeRef.current.clearValue();
    setTerminalSerial("");
    setApprovalStatus("approved");
    setApplicationReportData([]);
  };

  const fetchApplicationReportData = () => {
    if (!isFilterReady(filterByType, orgCode, grpId, terminalSerial) || loading) return;
    setLoading(true);
    api
      .get("/applications", {
        headers: {
          Action: approvalStatus === "approved" ? "V_ALL_APPR_APP" : "V_ALL_NON_APPR_APP",
          "Sort-Column": "version_code",
          "Sort-Order": "ASC",
          Filter: filterByType,
          Value: filterByVal,
          "Is-Pagination": "false",
          ...(approvalStatus === "approved" ? { "X-Additional-Info": true } : {}),
        },
      })
      .then((res) => {
        setApplicationReportData(res.data?.dataList ?? []);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const approvalOptions = [
    { value: "approved", label: "Approved" },
    { value: "pending", label: "Pending" },
  ];

  const customStyles = {
    control: (provided) => ({
      ...provided,
      padding: "10px",
    }),
  };

  return (
    <div>
      <Stack className="report-filters" spacing={2} direction="row" sx={{ marginBottom: 3 }}>
        <FilterByDropdown onChange={handleFilterByChange} selectRef={selectFilterTypeRef} value={filterByType} />
        <TextField name="terminalSerial" value={terminalSerial} type="text" variant="outlined" color="secondary" label="Terminal Serial" onChange={handleTerminalSerialChange} disabled={istrmnlDisabled} hidden={istrmnlDisabled} />
        <OrgsDropdown orgCode={orgCode ? orgCode : ""} onChange={handleOrgChange} isOrgDisabled={isOrgDisabled} selectRef={selectOrgRef} />
        <GroupsDropdown key={orgCode || "no-org"} orgCode={orgCode ? orgCode : ""} groupId={grpId ? grpId : ""} onChange={handleGrpChange} isGrpDisabled={isGrpDisabled || !orgCode} selectRef={selectGrpRef} />
        <div>
          <p
            style={{
              fontSize: "12px",
              position: "relative",
              top: "-9px",
              left: "10px",
              background: "#fff",
              width: "200px",
              paddingLeft: "5px",
              zIndex: 99,
            }}
          >
            Approval status
          </p>
          <FormControl
            required={false}
            sx={{
              minWidth: 400,
              zIndex: 90,
              position: "relative",
              top: "-32px",
              height: 0,
            }}
            size="medium"
          >
            <Select
              options={approvalOptions}
              value={approvalOptions.find((option) => option.value === approvalStatus)}
              onChange={(selectedOption) => setApprovalStatus(selectedOption?.value || "approved")}
              isClearable
              styles={{
                ...customStyles,
                menuPortal: (base) => ({
                  ...base,
                  zIndex: 9999,
                }),
              }}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </FormControl>
        </div>
      </Stack>

      <Stack className="report-actions" spacing={2} direction="row" sx={{ my: 3 }}>
        <Button variant="contained" color="warning" startIcon={<SearchIcon />} onClick={fetchApplicationReportData}
          disabled={loading || !isFilterReady(filterByType, orgCode, grpId, terminalSerial)}>
          Search
        </Button>
        <Button variant="outlined" color="error" startIcon={<ClearIcon />} onClick={clearFilters}>
          Clear
        </Button>
      </Stack>

      <ApplicationReportTable applicationReportData={applicationReportData} isLoading={loading} />
    </div>
  );
};

export default ApplicationReport;
